import { useState } from 'react';
import { Banner, Button, Card, Dialog, Field, formatMoney, Money, PageHeader, Pill, type Tone } from '@elysian/ui';
import type { RosVat3, VatReturn } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { longDate, shortDate } from '../invoices/shared';
import { MtdVat } from './MtdVat';

const STATE: Record<VatReturn['status'], { tone: Tone; label: (r: VatReturn) => string }> = {
  filed: { tone: 'ok', label: (r) => `Filed ${r.filed_at ? shortDate(r.filed_at.slice(0, 10)) : ''}`.trim() },
  overdue: { tone: 'danger', label: (r) => `Overdue · was due ${shortDate(r.ros_due_date ?? r.due_date)}` },
  due: { tone: 'warn', label: (r) => `Due ${shortDate(r.ros_due_date ?? r.due_date)}` },
  open: { tone: 'draft', label: () => 'Period still open' },
};

// The VAT3 boxes, in the order ROS shows them.
const BOXES: [keyof RosVat3, string][] = [
  ['T1', 'VAT on sales'], ['T2', 'VAT on purchases'], ['T3', 'Net payable'], ['T4', 'Net repayable'],
  ['E1', 'Goods to other EU countries'], ['E2', 'Goods from other EU countries'],
  ['ES1', 'Services to other EU countries'], ['ES2', 'Services from other EU countries'],
];

/** UK books send returns to HMRC (Making Tax Digital); Irish books prepare the VAT3 for ROS. */
export function VatReturns() {
  const { context } = useSession();
  if (context?.organisation.jurisdiction === 'UK') return <MtdVat />;
  return <RosVatReturns />;
}

function RosVatReturns() {
  const { source, context } = useSession();
  const [version, setVersion] = useState(0);
  const list = useAsync(() => source.vatReturns(), [source, version], { keep: true });
  const [selected, setSelected] = useState<string | null>(null);
  const returns = list.state === 'ready' ? list.data : [];
  const current = returns.find((r) => r.from === selected) ?? returns.find((r) => r.status === 'overdue' || r.status === 'due') ?? returns[0];
  const basis = context?.organisation.vat_basis === 'cash' ? 'Cash receipts basis' : 'Invoice basis';

  return (
    <>
      <PageHeader title="VAT returns" subtitle={`${basis} · two-monthly · figures for ROS VAT3`} />
      {list.state === 'error' && <Banner>Couldn’t load VAT returns: {list.error.message}</Banner>}
      {returns.filter((r) => r.status === 'overdue').map((r) => (
        <Banner key={r.from}>The {r.label} return was due on {longDate(r.ros_due_date ?? r.due_date)}. File it on ROS, then mark it filed here.</Banner>
      ))}
      <div className="el-two-col">
        <Card as="section" padding="16px" aria-labelledby="vat-periods">
          <h2 id="vat-periods" className="el-h2" style={{ marginBottom: 10 }}>Periods</h2>
          <div className="el-stack" style={{ gap: 6 }}>
            {returns.map((r) => {
              const st = STATE[r.status] ?? STATE.open;
              return (
                <button key={r.from} type="button" className="el-pick" aria-current={current?.from === r.from ? 'true' : undefined} onClick={() => setSelected(r.from)}>
                  <span className="el-pick-main">{r.label} {r.from.slice(0, 4)}<Money value={r.net_payable ?? '0'} /></span>
                  <Pill tone={st.tone}>{st.label(r)}</Pill>
                </button>
              );
            })}
          </div>
        </Card>
        {current && <ReturnDetail key={`${current.from}:${version}`} period={current} onFiled={() => setVersion((v) => v + 1)} />}
      </div>
    </>
  );
}

function ReturnDetail({ period, onFiled }: { period: VatReturn; onFiled: () => void }) {
  const { source, can } = useSession();
  const toast = useToast((s) => s.show);
  const ros = useAsync(() => source.rosVat3(period.from, period.to), [source, period.from]);
  const [filing, setFiling] = useState(false);
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const st = STATE[period.status] ?? STATE.open;
  const canFile = period.status !== 'filed' && period.status !== 'open' && can('vat.file');

  async function file() {
    setBusy(true);
    setError(null);
    try {
      await source.fileVat(period.from, period.to, reference);
      toast(`${period.label} marked as filed. Its months are now locked.`);
      setFiling(false);
      onFiled();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function csv() {
    const blob = await source.rosVat3Csv(period.from, period.to).catch(() => null);
    if (!blob) {
      toast('The CSV comes from the server. With demo data, copy the figures from the table.');
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `VAT3-${period.from}-${period.to}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  async function copy(v: number) {
    try {
      await navigator.clipboard.writeText(String(v));
      toast(`Copied ${v}`);
    } catch {
      toast('Copy isn’t available here; select the figure instead.');
    }
  }

  return (
    <Card as="section" padding="18px 20px" aria-labelledby="vat-detail">
      <div className="el-spread" style={{ marginBottom: 12 }}>
        <div>
          <h2 id="vat-detail" className="el-h2">{period.label} {period.from.slice(0, 4)}</h2>
          <div className="el-hint">{longDate(period.from)} to {longDate(period.to)}{period.reference && <> · ROS reference {period.reference}</>}</div>
        </div>
        <Pill tone={st.tone}>{st.label(period)}</Pill>
      </div>
      <div className="el-table-wrap">
        <table className="el-table" aria-label="VAT3 figures">
          <thead><tr><th scope="col">Box</th><th scope="col">What it is</th><th scope="col" className="el-num">In the books</th><th scope="col" className="el-num">For ROS (whole €)</th></tr></thead>
          <tbody>
            {BOXES.map(([box, label]) => {
              const exact = period.boxes?.[box] ?? '0';
              const r = ros.state === 'ready' ? (ros.data[box] as number | undefined) : undefined;
              return (
                <tr key={box}>
                  <td><strong>{box}</strong></td>
                  <td>{label}</td>
                  <td className="el-num"><Money value={exact} /></td>
                  <td className="el-num">
                    {r === undefined ? '…' : <button type="button" className="el-copy" onClick={() => void copy(r)} aria-label={`Copy ${box}: ${r}`}>{r.toLocaleString('en-IE')}</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="el-hint">
        ROS takes whole euro, rounded to the nearest euro. Tap a figure to copy it.
        {period.status === 'open' && ' The period is still open, so these figures will change as you add invoices and bills.'}
      </p>
      <div className="el-row" style={{ justifyContent: 'flex-end' }}>
        <Button onClick={() => void csv()}>Download CSV</Button>
        {canFile && <Button variant="primary" onClick={() => { setError(null); setReference(''); setFiling(true); }}>Mark as filed on ROS</Button>}
      </div>

      {filing && (
        <Dialog title={`Mark ${period.label} as filed?`} onClose={() => setFiling(false)}
          actions={<><Button variant="ghost" onClick={() => setFiling(false)}>Not yet</Button><Button variant="primary" disabled={busy || !reference.trim()} onClick={() => void file()}>Mark as filed</Button></>}>
          <p style={{ margin: 0 }}>
            Net {Number(period.net_payable ?? 0) >= 0 ? 'payable' : 'repayable'}: <strong>{formatMoney((period.net_payable ?? '0').replace('-', ''))}</strong>.
            Filing locks {longDate(period.from)} to {longDate(period.to)}, so nothing in those months can change afterwards. A backup is taken first.
          </p>
          <Field id="ros-ref" label="ROS acknowledgement number">
            <input id="ros-ref" className="el-input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="ROS-1234567" />
          </Field>
          {error && <div className="el-error-box" role="alert">{error}</div>}
        </Dialog>
      )}
    </Card>
  );
}
